import { __assign } from 'tslib';
import React from 'react';
import { useIntl } from 'react-intl';
import { CloseIcon } from '@panwds/icons';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import '@popperjs/core';
import { testIdGen } from '../../utils/testIdGen.esm.js';
import { TruncateText } from '../TruncateText/TruncateText.esm.js';

var closeAppearance = {
    standard: "hover:tw-bg-blue-200",
    simple: "hover:tw-bg-blue-200",
    blue: "hover:tw-bg-blue-400",
    green: "hover:tw-bg-green-400",
    yellow: "hover:tw-bg-yellow-400",
    orange: "hover:tw-bg-orange-400",
    red: "hover:tw-bg-red-400",
    gray: "hover:tw-bg-gray-400",
    teal: "hover:tw-bg-vis-d-400"
};
var sizes = {
    xs: "4rem",
    sm: "8rem",
    md: "16rem",
    lg: "24rem"
};
var Tag = function (_a) {
    var children = _a.children, _b = _a.appearance, appearance = _b === void 0 ? "standard" : _b, closeButtonProps = _a.closeButtonProps, dataMetrics = _a.dataMetrics, dataTestId = _a.dataTestId, icon = _a.icon, _c = _a.isSelected, isSelected = _c === void 0 ? false : _c, onClick = _a.onClick, _d = _a.readOnly, readOnly = _d === void 0 ? false : _d, _e = _a.size, size = _e === void 0 ? "sm" : _e;
    var intl = useIntl();
    /* istanbul ignore next */
    if (process.env.NODE_ENV === "development" && closeButtonProps && onClick) {
        console.error("Invalid props: Both onClick and closeButton props provided. Expected one or the other.");
    }
    var typeClassName = {
        standard: classNames("tw-border-solid tw-border tw-text-secondary dark:tw-border-gray-700 dark:tw-text-dark-bg-secondary", onClick && "hover:tw-bg-blue-100", readOnly
            ? "tw-border-none tw-bg-gray-200 dark:tw-bg-gray-700 dark:tw-text-dark-bg-disabled"
            : isSelected
                ? "tw-bg-blue-200 tw-border-blue-600 dark:tw-bg-blue-200 dark:tw-border-blue-300"
                : "tw-bg-white tw-border-gray-300 dark:tw-bg-gray-1000 dark:tw-bg-gray-700"),
        simple: classNames("tw-text-secondary dark:tw-text-dark-bg-secondary", onClick && "hover:tw-bg-blue-100", readOnly
            ? "tw-bg-gray-200 dark:tw-bg-gray-700"
            : isSelected
                ? "tw-bg-blue-200"
                : "tw-bg-white dark:tw-bg-gray-1000"),
        blue: classNames(onClick && "hover:tw-bg-blue-400", readOnly
            ? "tw-border-none tw-bg-gray-200 dark:tw-bg-gray-700 dark:tw-text-dark-bg-secondary"
            : isSelected
                ? "tw-bg-blue-400 tw-ring tw-custom-ring tw-ring-blue-200 tw-custom-ring-blue-200"
                : "tw-bg-blue-300 focus:tw-ring-blue-400"),
        green: classNames(onClick && "hover:tw-bg-green-400", readOnly
            ? "tw-border-none tw-bg-gray-200 dark:tw-bg-gray-700 dark:tw-text-dark-bg-secondary"
            : isSelected
                ? "tw-bg-green-400 tw-ring tw-custom-ring tw-ring-blue-200 tw-custom-ring-blue-200"
                : "tw-bg-green-300"),
        yellow: classNames(onClick && "hover:tw-bg-yellow-400", readOnly
            ? "tw-border-none tw-bg-gray-200 dark:tw-bg-gray-700 dark:tw-text-dark-bg-secondary"
            : isSelected
                ? "tw-bg-yellow-400 tw-ring tw-custom-ring tw-ring-blue-200 tw-custom-ring-blue-200"
                : "tw-bg-yellow-300"),
        orange: classNames(onClick && "hover:tw-bg-orange-400", readOnly
            ? "tw-border-none tw-bg-gray-200 dark:tw-bg-gray-700 dark:tw-text-dark-bg-secondary"
            : isSelected
                ? "tw-bg-orange-400 tw-ring tw-custom-ring tw-ring-blue-200 tw-custom-ring-blue-200"
                : "tw-bg-orange-300"),
        red: classNames(onClick && "hover:tw-bg-red-400", readOnly
            ? "tw-border-none tw-bg-gray-200 dark:tw-bg-gray-700 dark:tw-text-dark-bg-secondary"
            : isSelected
                ? "tw-bg-red-400 tw-ring tw-custom-ring tw-ring-blue-200 tw-custom-ring-blue-200"
                : "tw-bg-red-300"),
        gray: classNames(onClick && "hover:tw-bg-gray-400", readOnly
            ? "tw-border-none tw-bg-gray-200 dark:tw-bg-gray-700 dark:tw-text-dark-bg-secondary"
            : isSelected
                ? "tw-bg-gray-400 tw-ring tw-custom-ring tw-ring-blue-200 tw-custom-ring-blue-200"
                : "tw-bg-gray-300"),
        teal: classNames(onClick && "hover:tw-bg-vis-d-400", readOnly
            ? "tw-border-none tw-bg-gray-200 dark:tw-bg-gray-700 dark:tw-text-dark-bg-secondary"
            : isSelected
                ? "tw-bg-vis-d-400 tw-ring tw-custom-ring tw-ring-blue-200 tw-custom-ring-blue-200"
                : "tw-bg-vis-d-600")
    };
    var classes = classNames("tw-font-lato tw-box-border tw-h-6 tw-py-1 tw-px-2 tw-rounded-full tw-text-xs", "tw-inline-flex tw-items-center tw-align-middle", typeClassName[appearance], readOnly && "tw-cursor-text", onClick &&
        "tw-box-border tw-p-0 tw-m-0 tw-border-0 focus:tw-outline-none focus:tw-ring focus:tw-custom-ring tw-cursor-pointer");
    var closeClassname = classNames("tw-box-border tw-border-none tw-bg-transparent tw-flex tw-ml-1 tw-mr-1", "tw-rounded-full focus:tw-outline-none", readOnly ? "tw-cursor-pointer" : "focus:tw-ring focus:tw-custom-ring", !readOnly && closeAppearance[appearance]);
    var closeButton = (React.createElement("button", __assign({ type: "button" }, closeButtonProps, { "aria-label": intl.formatMessage({ id: "P1wNW7", defaultMessage: [{ type: 0, value: "Remove" }] }), className: closeClassname, style: { padding: "1px" } }),
        React.createElement(CloseIcon, { size: "xxs", className: "tw-text-default dark:tw-text-secondary" })));
    var Element = onClick ? "button" : "div";
    return (React.createElement(Element, { "data-metrics": dataMetrics, "data-testid": dataTestId ||
            "tag-".concat(typeof children === "string" && testIdGen(children)), className: classes, onClick: onClick, style: { maxWidth: sizes[size] }, type: Element === "button" ? "button" : undefined },
        icon && React.createElement("span", { className: "tw-mr-1 tw-flex" }, icon),
        React.createElement(TruncateText, null, children),
        closeButtonProps && closeButton));
};

export { Tag };
//# sourceMappingURL=Tag.esm.js.map
